"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdventureParser = void 0;
exports.parseSubAdventureFile = parseSubAdventureFile;
const characterInstanceNames_1 = require("../utils/characterInstanceNames");
const activitySceneHelpers_1 = require("../activities/activitySceneHelpers");
const entityDependenceScan_1 = require("../entities/entityDependenceScan");
const computeEntityListRows_1 = require("../entities/computeEntityListRows");
const constants_1 = require("../entities/constants");
const Tokenizer_1 = require("./Tokenizer");
const RichTextParser_1 = require("./RichTextParser");
const styleValidation_1 = require("../utils/styleValidation");
const VariablesConfigParser_1 = require("./VariablesConfigParser");
const AdventurePreprocessor_1 = require("./AdventurePreprocessor");
const AdventureScriptsParser_1 = require("./AdventureScriptsParser");
const pathConfig_1 = require("../utils/pathConfig");
const adventureTags_1 = require("../utils/adventureTags");
const entityListForeign_1 = require("../utils/entityListForeign");
const fs = __importStar(require("fs"));
const path = __importStar(require("path"));
/**
 * Parse a single sub-adventure file (.sad or fragment). Use this to load only a .sad file
 * without requiring a full adventure (title, characters, etc.). Content is wrapped with a
 * minimal title if it does not start with "# " so the parser accepts it.
 * Uses the same parser and preprocessor as full adventures so format changes stay in sync.
 */
function parseSubAdventureFile(filePath, options) {
    const content = fs.readFileSync(filePath, 'utf-8');
    const defaultId = path.basename(filePath, path.extname(filePath));
    const adventureId = options?.adventureId ?? defaultId;
    let input = content;
    if (!content.trimStart().startsWith('# ')) {
        input = `# ${adventureId}\n\n` + content;
    }
    const basePath = path.dirname(filePath);
    const parser = new AdventureParser(basePath, options?.parserOptions);
    return parser.parse(input, adventureId);
}
class AdventureParser {
    constructor(basePath, parserOptions) {
        this.tokens = [];
        this.currentTokenIndex = 0;
        this.errors = [];
        this.currentSceneId = '';
        this.currentSectionIndex = 0;
        this.currentOptionIndex = 0;
        this.basePath = basePath;
        this.parserOptions = parserOptions;
    }
    parse(input, adventureId = 'unknown') {
        this.errors = [];
        let processedInput = input;
        // Preprocess sub-adventures and snippets if base path is provided
        if (this.basePath) {
            try {
                const preprocessorConfig = {
                    basePath: this.basePath,
                    ...(this.parserOptions && {
                        snippetsRoot: this.parserOptions.snippetsRoot,
                        adventureDirectory: this.parserOptions.adventureDirectory
                    })
                };
                const preprocessor = new AdventurePreprocessor_1.AdventurePreprocessor(preprocessorConfig);
                const result = preprocessor.process(processedInput, adventureId);
                // Log warnings if any
                if (result.warnings.length > 0) {
                    this.errors.push(...result.warnings.map(w => ({
                        line: 0,
                        message: w,
                        type: 'warning'
                    })));
                }
                processedInput = result.processedText;
            }
            catch (error) {
                // Add preprocessing errors to parse errors
                this.addError(0, error instanceof Error ? error.message : 'Unknown preprocessing error', 'syntax');
            }
        }
        const scriptsResult = AdventureScriptsParser_1.AdventureScriptsParser.processAdventureText(processedInput);
        for (const message of scriptsResult.errors) {
            this.addError(0, message, 'syntax');
        }
        processedInput = scriptsResult.textWithoutScriptsSection;
        const mergedAdventureScripts = scriptsResult.scripts;
        // Normalize line endings to Unix style (\n) to handle both Windows (\r\n) and Unix (\n) line endings
        const normalizedInput = processedInput.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const tokenizer = new Tokenizer_1.Tokenizer(normalizedInput);
        this.tokens = tokenizer.tokenize();
        this.currentTokenIndex = 0;
        // Check for tokenizer errors (like unclosed scripts)
        for (const token of this.tokens) {
            if (token.type === Tokenizer_1.TokenType.ERROR) {
                this.addError(token.line, token.value, 'syntax');
            }
        }
        try {
            const adventure = this.parseAdventure();
            if (mergedAdventureScripts !== undefined) {
                adventure.scripts = mergedAdventureScripts;
            }
            return {
                adventure,
                errors: this.errors
            };
        }
        catch (error) {
            this.addError(0, `Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`, 'syntax');
            return {
                adventure: this.createEmptyAdventure(),
                errors: this.errors
            };
        }
    }
    parseAdventure() {
        const adventure = {
            title: '',
            description: RichTextParser_1.RichTextParser.parseRichTextDescription(''),
            characters: [],
            players: [],
            scenes: [],
            items: [],
            locations: [],
            stats: [],
            activityTypes: [],
            activities: []
        };
        // Parse title
        if (this.currentToken().type === Tokenizer_1.TokenType.HEADER) {
            adventure.title = this.currentToken().value;
            this.advance();
        }
        else {
            // If the first token is not a header, this is an error
            this.addError(this.currentToken().line, 'Adventure must start with a title (line beginning with #)', 'validation');
        }
        // Skip newlines after title
        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
            this.advance();
        }
        // Parse description (must use Description: keyword with triple-quote delimiters)
        if (this.currentToken().type === Tokenizer_1.TokenType.DESCRIPTION) {
            const descLine = this.currentToken().line;
            this.advance(); // Skip the "Description:" token
            if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                const descriptionText = this.currentToken().value;
                // Parse as rich text description
                adventure.description = RichTextParser_1.RichTextParser.parseRichTextDescription(descriptionText);
                this.advance();
            }
            else {
                // Empty description - this is valid
                adventure.description = RichTextParser_1.RichTextParser.parseRichTextDescription('');
            }
        }
        // Skip newlines before checking for style and title image
        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
            this.advance();
        }
        // Parse optional preamble fields: TitleImage, Tags, TickIntervalMs, HideHeaderForGuests, Library (any order, repeatable)
        while (this.currentToken().type === Tokenizer_1.TokenType.TITLE_IMAGE ||
            this.currentToken().type === Tokenizer_1.TokenType.TAGS ||
            this.currentToken().type === Tokenizer_1.TokenType.TICK_INTERVAL_MS ||
            this.currentToken().type === Tokenizer_1.TokenType.HIDE_HEADER_FOR_GUESTS ||
            this.currentToken().type === Tokenizer_1.TokenType.LIBRARY) {
            if (this.currentToken().type === Tokenizer_1.TokenType.TITLE_IMAGE) {
                const titleImageLine = this.currentToken().line;
                this.advance(); // Skip the "TitleImage:" token
                if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                    const titleImageId = this.currentToken().value.trim();
                    if (titleImageId) {
                        // Validate title image ID format
                        if (this.isValidImageId(titleImageId)) {
                            adventure.titleImageId = titleImageId;
                            adventure.bannerId = titleImageId; // Banner always matches title image
                        }
                        else {
                            this.addError(titleImageLine, `Invalid TitleImage ID format: '${titleImageId}'. Use lowercase with underscores and hyphens (e.g., 'fantasy', 'sci_fi')`, 'warning');
                            adventure.titleImageId = 'generic';
                            adventure.bannerId = 'generic';
                        }
                    }
                    this.advance();
                }
            }
            else if (this.currentToken().type === Tokenizer_1.TokenType.TAGS) {
                const tagsLine = this.currentToken().line;
                this.advance();
                if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.currentToken().value.trim();
                    const { tags, invalidTokens } = (0, adventureTags_1.parseAdventureTagsLine)(raw);
                    adventure.tags = tags;
                    for (const invalid of invalidTokens) {
                        this.addError(tagsLine, `Invalid adventure tag '${invalid}'. Use lowercase letters, numbers, hyphens, and underscores.`, 'warning');
                    }
                    this.advance();
                }
                else {
                    adventure.tags = [];
                }
            }
            else if (this.currentToken().type === Tokenizer_1.TokenType.TICK_INTERVAL_MS) {
                const tickLine = this.currentToken().line;
                this.advance();
                if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.currentToken().value.trim();
                    const n = Number(raw);
                    if (Number.isFinite(n) && n > 0 && Math.floor(n) === n) {
                        adventure.tickIntervalMs = n;
                    }
                    else {
                        this.addError(tickLine, `Invalid TickIntervalMs: '${raw}'. Use a positive integer (milliseconds).`, 'warning');
                    }
                    this.advance();
                }
            }
            else if (this.currentToken().type === Tokenizer_1.TokenType.HIDE_HEADER_FOR_GUESTS) {
                const hideHeaderLine = this.currentToken().line;
                this.advance();
                if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.currentToken().value.trim().toLowerCase();
                    if (raw === 'true' || raw === 'yes') {
                        adventure.hideHeaderForGuests = true;
                    }
                    else if (raw === 'false' || raw === 'no') {
                        adventure.hideHeaderForGuests = false;
                    }
                    else {
                        this.addError(hideHeaderLine, `Invalid HideHeaderForGuests: '${this.currentToken().value.trim()}'. Use true or false.`, 'warning');
                    }
                    this.advance();
                }
            }
            else if (this.currentToken().type === Tokenizer_1.TokenType.LIBRARY) {
                const libraryLine = this.currentToken().line;
                this.advance();
                if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.currentToken().value.trim().toLowerCase();
                    if (raw === 'true' || raw === 'yes') {
                        adventure.library = true;
                    }
                    else if (raw === 'false' || raw === 'no') {
                        adventure.library = false;
                    }
                    else {
                        this.addError(libraryLine, `Invalid Library: '${this.currentToken().value.trim()}'. Use true or false.`, 'warning');
                    }
                    this.advance();
                }
            }
            while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
                this.advance();
            }
        }
        // Skip newlines before checking for style
        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
            this.advance();
        }
        // Parse adventure-level style (optional)
        if (this.currentToken().type === Tokenizer_1.TokenType.STYLE) {
            const styleLine = this.currentToken().line;
            this.advance(); // Skip the "Style:" token
            if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                const styleText = this.currentToken().value;
                const parsedStyle = (0, styleValidation_1.parseStyleText)(styleText);
                if (parsedStyle) {
                    const validation = (0, styleValidation_1.validateCustomStyle)(parsedStyle, 'Adventure style');
                    if (validation.isValid && validation.sanitizedStyle) {
                        adventure.style = validation.sanitizedStyle;
                    }
                    // Add any errors or warnings
                    this.errors.push(...(0, styleValidation_1.styleValidationToParseErrors)(validation, styleLine));
                }
                this.advance();
            }
        }
        // Skip newlines before checking for variables
        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
            this.advance();
        }
        // Parse adventure-level variables (optional)
        if (this.currentToken().type === Tokenizer_1.TokenType.VARIABLES) {
            this.advance(); // Skip the "Variables:" token
            adventure.variables = this.parseVariables('adventure');
        }
        // Default to 'generic' if titleImageId not specified
        if (!adventure.titleImageId) {
            adventure.titleImageId = 'generic';
            adventure.bannerId = 'generic';
        }
        // Parse sections
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF) {
            if (this.currentToken().type === Tokenizer_1.TokenType.SECTION) {
                const sectionType = this.currentToken().value.toLowerCase();
                this.advance();
                switch (sectionType) {
                    case 'sub-adventures':
                        adventure.subAdventures = this.parseSubAdventures();
                        break;
                    case 'variables':
                        // Sub-adventures can contribute adventure-level variables (merged into main)
                        const existingVars = adventure.variables || [];
                        // Skip newlines and optional "Variables:" line in section body
                        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE || this.currentToken().type === Tokenizer_1.TokenType.VARIABLES) {
                            this.advance();
                        }
                        adventure.variables = existingVars.concat(this.parseVariables('adventure'));
                        break;
                    case 'character variables':
                        adventure.characterVariables = this.parseCharacterVariables();
                        break;
                    case 'shared variables':
                        {
                            const existingShared = adventure.sharedVariables || [];
                            let sharedVariableScope = adventure.sharedVariableScope;
                            while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
                                this.advance();
                            }
                            while (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                                const line = this.currentToken().value.trim();
                                const scopeMatch = line.match(/^Scope:\s*([a-z][a-z0-9_]*)\s*$/i);
                                if (scopeMatch) {
                                    sharedVariableScope = scopeMatch[1].toLowerCase();
                                }
                                else if (line.length > 0) {
                                    this.addError(this.currentToken().line, `Unexpected line in Shared Variables section: ${line}`, 'syntax');
                                }
                                this.advance();
                                while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
                                    this.advance();
                                }
                            }
                            while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE || this.currentToken().type === Tokenizer_1.TokenType.VARIABLES) {
                                this.advance();
                            }
                            adventure.sharedVariables = this.mergeSharedVariables(existingShared, this.parseVariables('shared'));
                            if (sharedVariableScope) {
                                adventure.sharedVariableScope = sharedVariableScope;
                            }
                        }
                        break;
                    case 'characters':
                        adventure.characters = this.parseCharacters();
                        break;
                    case 'players':
                        adventure.players = this.parsePlayers();
                        break;
                    case 'scenes':
                        adventure.scenes = this.parseScenes();
                        break;
                    case 'items':
                        adventure.items = this.parseItems();
                        break;
                    case 'locations':
                        adventure.locations = this.parseLocations();
                        break;
                    case 'activity types':
                        adventure.activityTypes = this.parseActivityTypes();
                        break;
                    case 'activities':
                        adventure.activities = this.parseActivities();
                        break;
                    case 'entity types':
                        adventure.entityTypes = this.parseEntityTypes();
                        break;
                    case 'stats':
                        adventure.stats = this.parseStats();
                        break;
                    case 'scripts':
                        this.skipScriptsSection();
                        break;
                }
            }
            else {
                this.advance();
            }
        }
        // Inline option target validation is run in AdventureManager after required-scenes injection,
        // so that targets like session_info (injected from required_scenes) are present.
        // Validate adventure tags after parsing
        this.validateAdventureTags(adventure);
        // Validate player definitions
        this.validatePlayerDefinitions(adventure);
        this.validateLibraryAdventure(adventure);
        // Validate variable references
        this.validateVariableReferences(adventure);
        // Validate section ordering
        this.validateSectionOrder(adventure);
        // Validate scene location references
        this.validateSceneLocationReferences(adventure);
        this.validateActivities(adventure);
        this.validateEntityTypes(adventure);
        return adventure;
    }
    parseActivityTypes() {
        const types = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.ACTIVITY_TYPE_HEADING) {
                const t = this.parseActivityType();
                if (t) {
                    types.push(t);
                }
            }
            this.advance();
        }
        return types;
    }
    findActivityTypeBlockEnd(startIndex) {
        for (let i = startIndex; i < this.tokens.length; i++) {
            const t = this.tokens[i].type;
            if (t === Tokenizer_1.TokenType.SECTION ||
                t === Tokenizer_1.TokenType.ACTIVITY_TYPE_HEADING ||
                t === Tokenizer_1.TokenType.ACTIVITY_DEF ||
                t === Tokenizer_1.TokenType.ENTITY_TYPE_HEADING) {
                return i;
            }
        }
        return this.tokens.length;
    }
    findActivityBlockEnd(startIndex) {
        for (let i = startIndex; i < this.tokens.length; i++) {
            const t = this.tokens[i].type;
            if (t === Tokenizer_1.TokenType.SECTION ||
                t === Tokenizer_1.TokenType.ACTIVITY_DEF ||
                t === Tokenizer_1.TokenType.ACTIVITY_TYPE_HEADING ||
                t === Tokenizer_1.TokenType.ENTITY_TYPE_HEADING) {
                return i;
            }
        }
        return this.tokens.length;
    }
    parseActivityType() {
        const id = this.currentToken().value;
        this.advance();
        let displayName = '';
        let iconId;
        const startIndex = this.currentTokenIndex;
        const endIndex = this.findActivityTypeBlockEnd(startIndex);
        for (let i = startIndex; i < endIndex; i++) {
            const tok = this.tokens[i];
            if (tok.type === Tokenizer_1.TokenType.NAME && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!displayName) {
                    displayName = this.tokens[i + 1].value;
                }
            }
            if (tok.type === Tokenizer_1.TokenType.ICON && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!iconId) {
                    iconId = this.tokens[i + 1].value;
                }
            }
        }
        this.currentTokenIndex = endIndex > startIndex ? endIndex - 1 : startIndex - 1;
        if (!displayName.trim()) {
            this.addError(this.currentToken().line, `Activity type '${id}' missing Name`, 'validation');
            return null;
        }
        return { id, displayName: displayName.trim(), iconId };
    }
    parseActivities() {
        const activities = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.ACTIVITY_DEF) {
                const a = this.parseActivity();
                if (a) {
                    activities.push(a);
                }
            }
            this.advance();
        }
        return activities;
    }
    parseCommaSeparatedIds(raw) {
        if (!raw || !raw.trim()) {
            return [];
        }
        return raw.split(',').map(s => s.trim()).filter(Boolean);
    }
    parseActivity() {
        const id = this.currentToken().value;
        this.advance();
        let typeId = '';
        let name = '';
        let parentActivityId;
        let requiresCompleted = [];
        let requiresActive = [];
        let requiresScript;
        let eligibleCharacterNames = [];
        let startSceneId = '';
        let scenePrefix;
        let listOrder = 0;
        let hasListOrder = false;
        let description;
        let hideWhenCompleted;
        let initialiseScript;
        const startIndex = this.currentTokenIndex;
        const endIndex = this.findActivityBlockEnd(startIndex);
        for (let i = startIndex; i < endIndex; i++) {
            const tok = this.tokens[i];
            if (tok.type === Tokenizer_1.TokenType.ACTIVITY_FIELD_TYPE && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!typeId) {
                    typeId = this.tokens[i + 1].value.trim();
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.NAME && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!name) {
                    name = this.tokens[i + 1].value;
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ACTIVITY_PARENT && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!parentActivityId) {
                    parentActivityId = this.tokens[i + 1].value.trim();
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.REQUIRES_COMPLETED && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (requiresCompleted.length === 0) {
                    requiresCompleted = this.parseCommaSeparatedIds(this.tokens[i + 1].value);
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.REQUIRES_ACTIVE && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (requiresActive.length === 0) {
                    requiresActive = this.parseCommaSeparatedIds(this.tokens[i + 1].value);
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ELIGIBLE && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (eligibleCharacterNames.length === 0) {
                    eligibleCharacterNames = this.parseCommaSeparatedIds(this.tokens[i + 1].value);
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ACTIVITY_REQUIRES_SCRIPT && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                if (!requiresScript) {
                    requiresScript = this.tokens[i + 1].value;
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ACTIVITY_INITIALISE_SCRIPT && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                if (!initialiseScript) {
                    initialiseScript = this.tokens[i + 1].value;
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ACTIVITY_START_SCENE && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!startSceneId) {
                    startSceneId = this.tokens[i + 1].value.trim();
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.ACTIVITY_SCENE_PREFIX && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!scenePrefix) {
                    scenePrefix = this.tokens[i + 1].value.trim();
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.LIST_ORDER && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!hasListOrder) {
                    const n = parseInt(this.tokens[i + 1].value.trim(), 10);
                    if (!Number.isNaN(n)) {
                        listOrder = n;
                        hasListOrder = true;
                    }
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.DESCRIPTION && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!description) {
                    description = RichTextParser_1.RichTextParser.parseRichTextDescription(this.tokens[i + 1].value);
                }
            }
            else if (tok.type === Tokenizer_1.TokenType.HIDE_WHEN_COMPLETED && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (hideWhenCompleted === undefined) {
                    const v = this.tokens[i + 1].value.trim().toLowerCase();
                    hideWhenCompleted = v === 'true' || v === 'yes' || v === '1';
                }
            }
        }
        this.currentTokenIndex = endIndex > startIndex ? endIndex - 1 : startIndex - 1;
        if (!typeId) {
            this.addError(this.currentToken().line, `Activity '${id}' missing Type`, 'validation');
            return null;
        }
        if (!name.trim()) {
            this.addError(this.currentToken().line, `Activity '${id}' missing Name`, 'validation');
            return null;
        }
        if (!startSceneId) {
            this.addError(this.currentToken().line, `Activity '${id}' missing Start Scene`, 'validation');
            return null;
        }
        if (initialiseScript !== undefined && initialiseScript.trim() === '') {
            this.addError(this.currentToken().line, `Activity '${id}' has empty Initialise script`, 'warning');
            initialiseScript = undefined;
        }
        return {
            id,
            typeId,
            name: name.trim(),
            parentActivityId,
            requiresCompleted,
            requiresActive,
            requiresScript,
            eligibleCharacterNames,
            startSceneId,
            ...(scenePrefix ? { scenePrefix } : {}),
            listOrder,
            description,
            ...(hideWhenCompleted ? { hideWhenCompleted: true } : {}),
            ...(initialiseScript ? { initialiseScript } : {})
        };
    }
    parseEntityTypes() {
        const types = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.ENTITY_TYPE_HEADING) {
                const t = this.parseEntityType();
                if (t) {
                    types.push(t);
                }
            }
            this.advance();
        }
        return types;
    }
    findEntityTypeBlockEnd(startIndex) {
        for (let i = startIndex; i < this.tokens.length; i++) {
            const t = this.tokens[i].type;
            if (t === Tokenizer_1.TokenType.SECTION ||
                t === Tokenizer_1.TokenType.ENTITY_TYPE_HEADING ||
                t === Tokenizer_1.TokenType.ACTIVITY_TYPE_HEADING ||
                t === Tokenizer_1.TokenType.ACTIVITY_DEF) {
                return i;
            }
        }
        return this.tokens.length;
    }
    parseEntityType() {
        const id = this.currentToken().value;
        this.advance();
        let displayName = '';
        let iconId;
        let startSceneId = '';
        let scenePrefix;
        let allowedParentTypeIds = [];
        let allowedChildTypeIds = [];
        let variables = [];
        const startIndex = this.currentTokenIndex;
        const endIndex = this.findEntityTypeBlockEnd(startIndex);
        for (let i = startIndex; i < endIndex; i++) {
            const tok = this.tokens[i];
            if (tok.type === Tokenizer_1.TokenType.NAME && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!displayName) {
                    displayName = this.tokens[i + 1].value;
                }
            }
            if (tok.type === Tokenizer_1.TokenType.ICON && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                if (!iconId) {
                    iconId = this.tokens[i + 1].value;
                }
            }
            if (tok.type === Tokenizer_1.TokenType.ACTIVITY_START_SCENE && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                startSceneId = this.tokens[i + 1].value.trim();
            }
            if (tok.type === Tokenizer_1.TokenType.ACTIVITY_SCENE_PREFIX && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                scenePrefix = this.tokens[i + 1].value.trim();
            }
            if (tok.type === Tokenizer_1.TokenType.ALLOWED_PARENTS && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                allowedParentTypeIds = this.parseCommaSeparatedIds(this.tokens[i + 1].value);
            }
            if (tok.type === Tokenizer_1.TokenType.ALLOWED_CHILDREN && i + 1 < endIndex && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                allowedChildTypeIds = this.parseCommaSeparatedIds(this.tokens[i + 1].value);
            }
        }
        const varsTokIndex = this.tokens.findIndex((t, idx) => idx >= startIndex && idx < endIndex && t.type === Tokenizer_1.TokenType.VARIABLES);
        if (varsTokIndex >= 0) {
            const saved = this.currentTokenIndex;
            this.currentTokenIndex = varsTokIndex;
            this.advance();
            variables = this.parseVariables('entity').map(v => ({
                ...v,
                ownerScope: 'entity',
                ownerId: id
            }));
            this.currentTokenIndex = saved;
        }
        this.currentTokenIndex = endIndex > startIndex ? endIndex - 1 : startIndex - 1;
        if (!displayName.trim()) {
            this.addError(this.currentToken().line, `Entity type '${id}' missing Name`, 'validation');
            return null;
        }
        if (!startSceneId.trim()) {
            this.addError(this.currentToken().line, `Entity type '${id}' missing Start Scene`, 'validation');
            return null;
        }
        return {
            id,
            displayName: displayName.trim(),
            iconId,
            startSceneId: startSceneId.trim(),
            scenePrefix,
            allowedParentTypeIds,
            allowedChildTypeIds,
            variables
        };
    }
    validateActivities(adventure) {
        const activities = adventure.activities || [];
        const types = adventure.activityTypes || [];
        if (activities.length === 0) {
            return;
        }
        if (types.length === 0) {
            this.addError(0, '## Activities is present but ## Activity Types is empty or missing. Define activity types when using activities.', 'validation');
            return;
        }
        const typeIds = new Set(types.map(t => t.id));
        const activityIds = new Set();
        const activityById = new Map();
        for (const a of activities) {
            if (activityIds.has(a.id)) {
                this.addError(0, `Duplicate activity id: '${a.id}'`, 'validation');
            }
            activityIds.add(a.id);
            activityById.set(a.id, a);
            if (!typeIds.has(a.typeId)) {
                this.addError(0, `Activity '${a.id}' references unknown activity type '${a.typeId}'`, 'validation');
            }
        }
        const sceneIds = new Set((adventure.scenes || []).map(s => s.id));
        const characterNames = new Set((adventure.characters || []).map(c => c.name));
        for (const a of activities) {
            for (const req of a.requiresCompleted) {
                if (!activityById.has(req)) {
                    this.addError(0, `Activity '${a.id}' Requires Completed references unknown activity '${req}'`, 'validation');
                }
            }
            for (const req of a.requiresActive) {
                if (!activityById.has(req)) {
                    this.addError(0, `Activity '${a.id}' Requires Active references unknown activity '${req}'`, 'validation');
                }
            }
            if (a.parentActivityId) {
                if (!activityById.has(a.parentActivityId)) {
                    this.addError(0, `Activity '${a.id}' Parent references unknown activity '${a.parentActivityId}'`, 'validation');
                }
                else {
                    const seen = new Set();
                    let cur = a.parentActivityId;
                    while (cur) {
                        if (seen.has(cur)) {
                            this.addError(0, `Activity parent chain cycle involving '${a.id}'`, 'validation');
                            break;
                        }
                        seen.add(cur);
                        cur = activityById.get(cur)?.parentActivityId;
                    }
                }
            }
            if (!sceneIds.has(a.startSceneId)) {
                this.addError(0, `Activity '${a.id}' Start Scene references unknown scene '${a.startSceneId}'`, 'validation');
            }
            if (a.scenePrefix) {
                const expectedStartPrefix = `${a.scenePrefix}${activitySceneHelpers_1.ACTIVITY_SCENE_ID_DELIMITER}`;
                if (a.startSceneId !== a.scenePrefix &&
                    !a.startSceneId.startsWith(expectedStartPrefix)) {
                    this.addError(0, `Activity '${a.id}' Start Scene '${a.startSceneId}' should use Scene Prefix '${a.scenePrefix}' (expected id starting with '${expectedStartPrefix}')`, 'warning');
                }
            }
            for (const c of a.eligibleCharacterNames) {
                if (!characterNames.has(c)) {
                    this.addError(0, `Activity '${a.id}' Eligible references unknown character '${c}'`, 'validation');
                }
            }
        }
        const visiting = new Set();
        const visited = new Set();
        const dfs = (node) => {
            if (visiting.has(node)) {
                return true;
            }
            if (visited.has(node)) {
                return false;
            }
            visiting.add(node);
            const def = activityById.get(node);
            if (def) {
                for (const nxt of def.requiresCompleted) {
                    if (activityById.has(nxt) && dfs(nxt)) {
                        return true;
                    }
                }
            }
            visiting.delete(node);
            visited.add(node);
            return false;
        };
        for (const a of activities) {
            if (!visited.has(a.id) && dfs(a.id)) {
                this.addError(0, `Cycle detected in Requires Completed graph (involving activity '${a.id}')`, 'validation');
                break;
            }
        }
    }
    validateEntityTypes(adventure) {
        const entityTypes = adventure.entityTypes || [];
        const sceneIds = new Set((adventure.scenes || []).map(s => s.id));
        const typeIds = new Set(entityTypes.map(t => t.id));
        // Collect declared names from other scopes for overlap checks
        const otherScopeNames = new Set();
        for (const v of adventure.variables || []) {
            otherScopeNames.add(v.name);
        }
        for (const v of adventure.characterVariables || []) {
            otherScopeNames.add(v.name);
        }
        for (const v of adventure.sharedVariables || []) {
            otherScopeNames.add(v.name);
        }
        for (const c of adventure.characters || []) {
            for (const v of c.variables || []) {
                otherScopeNames.add(v.name);
            }
        }
        for (const s of adventure.scenes || []) {
            for (const v of s.variables || []) {
                otherScopeNames.add(v.name);
            }
        }
        const seenTypeIds = new Set();
        for (const t of entityTypes) {
            if (seenTypeIds.has(t.id)) {
                this.addError(0, `Duplicate entity type id: '${t.id}'`, 'validation');
            }
            seenTypeIds.add(t.id);
            if (!sceneIds.has(t.startSceneId)) {
                this.addError(0, `Entity type '${t.id}' Start Scene references unknown scene '${t.startSceneId}'`, 'validation');
            }
            for (const p of t.allowedParentTypeIds) {
                if (!typeIds.has(p)) {
                    this.addError(0, `Entity type '${t.id}' Allowed Parents references unknown type '${p}'`, 'validation');
                }
            }
            for (const c of t.allowedChildTypeIds) {
                if (!typeIds.has(c)) {
                    this.addError(0, `Entity type '${t.id}' Allowed Children references unknown type '${c}'`, 'validation');
                }
            }
            for (const v of t.variables) {
                if (otherScopeNames.has(v.name)) {
                    this.addError(0, `Entity type '${t.id}' variable '${v.name}' overlaps a scene/adventure/character/shared variable name`, 'validation');
                }
            }
        }
        // Entity-List embeds: parent=current vs parentVariable mutual exclusion + parentVariable declared
        const stringVarNames = new Set();
        for (const v of adventure.variables || []) {
            if (v.type === 'string')
                stringVarNames.add(v.name);
        }
        for (const v of adventure.characterVariables || []) {
            if (v.type === 'string')
                stringVarNames.add(v.name);
        }
        const walkRichText = (desc, ctx) => {
            if (!desc || typeof desc === 'string')
                return;
            const visit = (els) => {
                if (!els)
                    return;
                for (const el of els) {
                    if (el.type === 'entity-list') {
                        const sourceRaw = (el.entityListSource || '').trim();
                        const isForeign = sourceRaw.length > 0;
                        if (!isForeign && entityTypes.length === 0) {
                            this.addError(0, `${ctx}: <Entity-List> requires ## Entity Types`, 'validation');
                        }
                        const hasParent = el.entityListParent !== undefined && el.entityListParent !== '';
                        const hasParentVar = el.entityListParentVariable !== undefined && el.entityListParentVariable !== '';
                        if (hasParent && hasParentVar) {
                            this.addError(0, `${ctx}: <Entity-List> cannot set both parent and parentVariable`, 'validation');
                        }
                        if (hasParent && el.entityListParent !== 'current') {
                            this.addError(0, `${ctx}: <Entity-List> parent must be 'current' (got '${el.entityListParent}')`, 'validation');
                        }
                        if (hasParentVar && !stringVarNames.has(el.entityListParentVariable)) {
                            this.addError(0, `${ctx}: <Entity-List> parentVariable '${el.entityListParentVariable}' must be a declared string adventure/character variable`, 'validation');
                        }
                        if ((el.entityListSelectionTarget && !el.entityListSelectionVariable) ||
                            (!el.entityListSelectionTarget && el.entityListSelectionVariable)) {
                            this.addError(0, `${ctx}: <Entity-List> selectionTarget and selectionVariable must be paired`, 'validation');
                        }
                        if (el.entityListSelectionVariable &&
                            !stringVarNames.has(el.entityListSelectionVariable)) {
                            this.addError(0, `${ctx}: <Entity-List> selectionVariable '${el.entityListSelectionVariable}' must be a declared string adventure/character variable`, 'validation');
                        }
                        const contentVariable = (el.entityListContentVariable || '').trim();
                        if (contentVariable) {
                            if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(contentVariable)) {
                                this.addError(0, `${ctx}: <Entity-List> contentVariable '${contentVariable}' must be a valid variable identifier`, 'validation');
                            }
                            else if (!isForeign) {
                                const typeFilter = (0, computeEntityListRows_1.parseEntityListTypesAttr)(el.entityListTypes);
                                const typesToCheck = typeFilter && typeFilter.length > 0
                                    ? entityTypes.filter(t => typeFilter.includes(t.id))
                                    : entityTypes;
                                for (const t of typesToCheck) {
                                    const declared = (t.variables || []).find(v => v.name === contentVariable);
                                    if (!declared || declared.type !== 'string') {
                                        this.addError(0, `${ctx}: <Entity-List> contentVariable '${contentVariable}' must be a declared string variable on entity type '${t.id}'`, 'validation');
                                    }
                                }
                            }
                        }
                        const filterVariable = (el.entityListFilterVariable || '').trim();
                        if (filterVariable) {
                            if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(filterVariable)) {
                                this.addError(0, `${ctx}: <Entity-List> filterVariable '${filterVariable}' must be a valid variable identifier`, 'validation');
                            }
                            else if (!isForeign) {
                                const typeFilter = (0, computeEntityListRows_1.parseEntityListTypesAttr)(el.entityListTypes);
                                const typesToCheck = typeFilter && typeFilter.length > 0
                                    ? entityTypes.filter(t => typeFilter.includes(t.id))
                                    : entityTypes;
                                for (const t of typesToCheck) {
                                    const declared = (t.variables || []).find(v => v.name === filterVariable);
                                    if (!declared || declared.type !== 'string') {
                                        this.addError(0, `${ctx}: <Entity-List> filterVariable '${filterVariable}' must be a declared string variable on entity type '${t.id}'`, 'validation');
                                    }
                                }
                            }
                        }
                        if (!filterVariable && el.entityListFilterValue !== undefined) {
                            this.addError(0, `${ctx}: <Entity-List> filterValue requires filterVariable`, 'validation');
                        }
                        const wantsReorder = !!el.entityListReorder;
                        const reorderCondition = (el.entityListReorderCondition || '').trim();
                        if (reorderCondition && !wantsReorder) {
                            this.addError(0, `${ctx}: <Entity-List> reorderCondition requires reorder=true`, 'validation');
                        }
                        if (wantsReorder) {
                            if (isForeign) {
                                this.addError(0, `${ctx}: <Entity-List> reorder is not supported on foreign (source=) lists`, 'validation');
                            }
                            const sortRaw = (el.entityListSort || '').trim().toLowerCase();
                            if (sortRaw !== 'position') {
                                this.addError(0, `${ctx}: <Entity-List> reorder=true requires sort=position`, 'validation');
                            }
                            if (!isForeign) {
                                const typeFilter = (0, computeEntityListRows_1.parseEntityListTypesAttr)(el.entityListTypes);
                                const typesToCheck = typeFilter && typeFilter.length > 0
                                    ? entityTypes.filter(t => typeFilter.includes(t.id))
                                    : entityTypes;
                                for (const t of typesToCheck) {
                                    const declared = (t.variables || []).find(v => v.name === constants_1.ENTITY_POSITION_VAR);
                                    if (!declared || declared.type !== 'string') {
                                        this.addError(0, `${ctx}: <Entity-List> reorder=true requires a declared string '${constants_1.ENTITY_POSITION_VAR}' variable on entity type '${t.id}'`, 'validation');
                                    }
                                }
                            }
                        }
                        if (isForeign) {
                            const parsedSource = (0, entityListForeign_1.parseEntityListSourceAttr)(sourceRaw);
                            if (!parsedSource || !parsedSource.ok) {
                                this.addError(0, `${ctx}: <Entity-List> ${parsedSource && !parsedSource.ok
                                    ? parsedSource.reason
                                    : `invalid source '${sourceRaw}'`}`, 'validation');
                            }
                            if (hasParent || hasParentVar) {
                                this.addError(0, `${ctx}: <Entity-List> with source cannot use parent or parentVariable (foreign hierarchical lists are not supported in v1)`, 'validation');
                            }
                            const hasSelection = !!(el.entityListSelectionTarget && el.entityListSelectionVariable);
                            if (!hasSelection) {
                                this.addError(0, `${ctx}: <Entity-List> with source requires selectionTarget and selectionVariable (reference-only; visit mode is not supported for foreign lists)`, 'validation');
                            }
                            const owner = (el.entityListOwner || '').trim();
                            const ownerVariable = (el.entityListOwnerVariable || '').trim();
                            const hasOwner = owner.length > 0;
                            const hasOwnerVariable = ownerVariable.length > 0;
                            if (hasOwner && hasOwnerVariable) {
                                this.addError(0, `${ctx}: <Entity-List> cannot set both owner and ownerVariable`, 'validation');
                            }
                            else if (!hasOwner && !hasOwnerVariable) {
                                this.addError(0, `${ctx}: <Entity-List> with source requires owner=self or ownerVariable=<string variable>`, 'validation');
                            }
                            else if (hasOwner && owner !== 'self') {
                                this.addError(0, `${ctx}: <Entity-List> owner must be 'self' (got '${owner}'); use ownerVariable for another user's library`, 'validation');
                            }
                            else if (hasOwnerVariable && !stringVarNames.has(ownerVariable)) {
                                this.addError(0, `${ctx}: <Entity-List> ownerVariable '${ownerVariable}' must be a declared string adventure/character variable`, 'validation');
                            }
                        }
                        else if ((el.entityListOwner && el.entityListOwner.trim()) ||
                            (el.entityListOwnerVariable && el.entityListOwnerVariable.trim())) {
                            this.addError(0, `${ctx}: <Entity-List> owner / ownerVariable require source=adventure:<adventureId>`, 'validation');
                        }
                    }
                    if (Array.isArray(el.content))
                        visit(el.content);
                    if (el.children)
                        visit(el.children);
                    if (el.elseChildren)
                        visit(el.elseChildren);
                }
            };
            visit(desc.elements);
        };
        for (const scene of adventure.scenes || []) {
            for (const section of scene.sections || []) {
                walkRichText(section.description, `Scene '${scene.id}'`);
                for (const opt of section.options || []) {
                    walkRichText(opt.text, `Scene '${scene.id}' option`);
                    walkRichText(opt.waitMessage, `Scene '${scene.id}' wait message`);
                }
            }
        }
        for (const msg of (0, entityDependenceScan_1.applyEntityDependenceScan)(adventure)) {
            this.addError(0, msg, 'validation');
        }
    }
    /**
     * Library adventures (`Library: true`) must be single-player (exactly one Players definition).
     */
    validateLibraryAdventure(adventure) {
        if (adventure.library !== true) {
            return;
        }
        const playerCount = adventure.players?.length ?? 0;
        if (playerCount !== 1) {
            this.addError(0, `Library: true adventures must declare exactly one ## Players definition (found ${playerCount})`, 'validation');
        }
    }
    validateSceneLocationReferences(adventure) {
        const locationTags = new Set((adventure.locations || []).map(location => location.tag));
        for (const scene of adventure.scenes || []) {
            if (scene.locationTag && !locationTags.has(scene.locationTag)) {
                this.addError(0, `Scene '${scene.id}' references unknown location '${scene.locationTag}'`, 'validation');
            }
        }
    }
    validateSectionOrder(adventure) {
        // Sub-Adventures section must appear before Players section if both exist
        // This is already enforced by preprocessing, but we add this for parser validation
        // The preprocessor handles sub-adventure inlining before players are parsed
        if (adventure.subAdventures && adventure.subAdventures.length > 0 && adventure.players && adventure.players.length > 0) {
            // This is informational - the preprocessor already handled the inlining
            // Sub-adventures are used by the preprocessor before parsing
        }
    }
    parseCharacters() {
        const characters = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.CHARACTER) {
                const character = this.parseCharacter();
                if (character) {
                    characters.push(character);
                }
            }
            this.advance();
        }
        return characters;
    }
    parsePlayers() {
        const players = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.PLAYER) {
                const player = this.parsePlayer();
                if (player) {
                    players.push(player);
                }
            }
            this.advance();
        }
        return players;
    }
    parseCharacterVariables() {
        const variablesMap = new Map();
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.VARIABLES) {
                this.advance(); // Skip the "Variables:" token
                const parsed = this.parseVariables('character');
                for (const v of parsed) {
                    variablesMap.set(v.name, v); // Later declarations override earlier
                }
                // parseVariables stops at the token after the last variable; don't advance past SECTION
                continue;
            }
            this.advance();
        }
        return Array.from(variablesMap.values());
    }
    mergeSharedVariables(existing, incoming) {
        const variablesMap = new Map();
        for (const v of existing) {
            variablesMap.set(v.name, v);
        }
        for (const v of incoming) {
            variablesMap.set(v.name, v);
        }
        return Array.from(variablesMap.values());
    }
    /** Skip `## Scripts` body if stripping left the section in the token stream. */
    skipScriptsSection() {
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            this.advance();
        }
    }
    parseSubAdventures() {
        const subAdventures = [];
        const seen = new Set();
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            // Look for bullet list items (they appear as TEXT tokens starting with '- ')
            if (this.currentToken().type === Tokenizer_1.TokenType.TEXT) {
                const text = this.currentToken().value.trim();
                if (text.startsWith('- ')) {
                    const subAdventureName = text.substring(2).trim();
                    // Validate sub-adventure name
                    if (!subAdventureName) {
                        this.addError(this.currentToken().line, 'Sub-adventure name cannot be empty', 'validation');
                    }
                    else if (subAdventureName.includes(' ')) {
                        this.addError(this.currentToken().line, `Invalid sub-adventure name: '${subAdventureName}'. Sub-directory paths are supported (e.g., 'ui/character-page')`, 'warning');
                    }
                    // Check for duplicates
                    if (seen.has(subAdventureName)) {
                        this.addError(this.currentToken().line, `Duplicate sub-adventure reference: '${subAdventureName}'`, 'validation');
                    }
                    else {
                        subAdventures.push(subAdventureName);
                        seen.add(subAdventureName);
                    }
                }
            }
            this.advance();
        }
        return subAdventures;
    }
    parsePlayer() {
        const playerName = this.currentToken().value;
        this.advance();
        let startingCharacter = '';
        let kind;
        let min;
        let max;
        const startIndex = this.currentTokenIndex;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.PLAYER) {
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.STARTING_CHARACTER) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    startingCharacter = this.tokens[i + 1].value;
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.KIND) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.tokens[i + 1].value.trim().toLowerCase();
                    if (raw === 'open' || raw === 'fixed') {
                        kind = raw;
                    }
                    else {
                        this.addError(this.tokens[i].line, `Player '${playerName}' has invalid Kind: '${this.tokens[i + 1].value}'. Use 'open' or 'fixed'.`, 'validation');
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.MIN) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.tokens[i + 1].value.trim();
                    const parsed = Number(raw);
                    if (!/^\d+$/.test(raw) || !Number.isInteger(parsed)) {
                        this.addError(this.tokens[i].line, `Player '${playerName}' has invalid Min: '${raw}'. Use a non-negative integer.`, 'validation');
                    }
                    else {
                        min = parsed;
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.MAX) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.tokens[i + 1].value.trim();
                    const parsed = Number(raw);
                    if (!/^\d+$/.test(raw) || !Number.isInteger(parsed) || parsed < 1) {
                        this.addError(this.tokens[i].line, `Player '${playerName}' has invalid Max: '${raw}'. Use an integer ≥ 1.`, 'validation');
                    }
                    else {
                        max = parsed;
                    }
                }
            }
        }
        if (!startingCharacter) {
            this.addError(this.currentToken().line, `Player '${playerName}' missing starting character`, 'validation');
            return null;
        }
        const effectiveKind = kind ?? 'fixed';
        if (effectiveKind === 'fixed' && (min !== undefined || max !== undefined)) {
            this.addError(this.currentToken().line, `Player '${playerName}' is fixed and must not declare Min/Max (use Kind: open for expandable seats).`, 'validation');
        }
        const result = {
            playerName,
            startingCharacter
        };
        if (kind && kind !== 'fixed') {
            result.kind = kind;
        }
        if (effectiveKind === 'open') {
            result.kind = 'open';
            if (min !== undefined) {
                result.min = min;
            }
            if (max !== undefined) {
                result.max = max;
            }
        }
        return result;
    }
    parseCharacter() {
        const name = this.currentToken().value;
        this.advance();
        let description = '';
        let startingScene = '';
        let portraitId = 'generic'; // Default to 'generic'
        let style;
        // Look ahead for description, starting scene, portrait, and style
        const startIndex = this.currentTokenIndex;
        // Find description
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const descriptionText = this.tokens[i + 1].value;
                    // Parse as rich text description
                    description = RichTextParser_1.RichTextParser.parseRichTextDescription(descriptionText);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Find starting scene
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.STARTING_SCENE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    startingScene = this.tokens[i + 1].value;
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Find portrait
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.PORTRAIT) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const parsedPortraitId = this.tokens[i + 1].value.trim();
                    // If empty, default to 'generic'
                    const trimmedPortraitId = parsedPortraitId.trim();
                    if (!trimmedPortraitId) {
                        portraitId = 'generic';
                    }
                    else if (trimmedPortraitId.startsWith('${') && trimmedPortraitId.endsWith('}')) {
                        portraitId = trimmedPortraitId;
                    }
                    else if (trimmedPortraitId === 'generic' || this.isValidPortraitId(trimmedPortraitId)) {
                        portraitId = trimmedPortraitId;
                    }
                    else {
                        this.addError(this.tokens[i].line, `Character '${name}' has invalid portrait ID format: '${parsedPortraitId}'. Use lowercase with underscores, namespaced format (e.g., 'adventurer_male' or 'adventure-id:portrait_id'), or a variable placeholder like \${user_portrait_id}. Defaulting to 'generic'.`, 'warning');
                        portraitId = 'generic';
                    }
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Find style
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.STYLE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const styleText = this.tokens[i + 1].value;
                    const parsedStyle = (0, styleValidation_1.parseStyleText)(styleText);
                    if (parsedStyle) {
                        const validation = (0, styleValidation_1.validateCustomStyle)(parsedStyle, `Character '${name}' style`);
                        if (validation.isValid && validation.sanitizedStyle) {
                            style = validation.sanitizedStyle;
                        }
                        // Add any errors or warnings
                        this.errors.push(...(0, styleValidation_1.styleValidationToParseErrors)(validation, this.tokens[i].line));
                    }
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Find variables
        let variables;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.VARIABLES) {
                // Store current position and parse variables
                const savedIndex = this.currentTokenIndex;
                this.currentTokenIndex = i;
                this.advance(); // Skip the "Variables:" token
                variables = this.parseVariables('character');
                this.currentTokenIndex = savedIndex; // Restore position
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // IsNpc: true / false / yes / no (optional)
        let isNpc;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.IS_NPC) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const v = this.tokens[i + 1].value.trim().toLowerCase();
                    isNpc = v === 'true' || v === 'yes' || v === '1';
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Kind: open | fixed (optional; default fixed)
        let kind;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.KIND) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const raw = this.tokens[i + 1].value.trim().toLowerCase();
                    if (raw === 'open' || raw === 'fixed') {
                        kind = raw;
                    }
                    else {
                        this.addError(this.tokens[i].line, `Character '${name}' has invalid Kind: '${this.tokens[i + 1].value}'. Use 'open' or 'fixed'.`, 'validation');
                    }
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        // Update: '''script''' (optional)
        let updateScript;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.NPC_UPDATE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                    updateScript = this.tokens[i + 1].value;
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.CHARACTER) {
                break;
            }
        }
        if (!startingScene) {
            this.addError(this.currentToken().line, `Character '${name}' missing starting scene`, 'validation');
            return null;
        }
        const character = {
            name,
            description,
            startingScene,
            portraitId,
            style,
            variables,
            isNpc,
            updateScript
        };
        if (kind && kind !== 'fixed') {
            character.kind = kind;
        }
        return character;
    }
    isValidPortraitId(portraitId) {
        // Portrait IDs can be:
        // 1. Simple format: lowercase alphanumeric with underscores (e.g., "generic", "art_director")
        // 2. Namespaced format: {adventure-id}:{portrait-id} (e.g., "styling-exploration:art_director")
        if (portraitId.includes(':')) {
            const parts = portraitId.split(':');
            if (parts.length !== 2)
                return false;
            const [adventureId, localId] = parts;
            return /^[a-z0-9-]+$/.test(adventureId) && /^[a-z0-9_]+$/.test(localId);
        }
        return /^[a-z0-9_]+$/.test(portraitId);
    }
    isValidImageId(imageId) {
        // Image IDs (title images, banners) can be:
        // 1. Simple format: lowercase alphanumeric with underscores/hyphens (e.g., "fantasy", "sci_fi")
        // 2. Namespaced format: {adventure-id}:{image-id} (e.g., "styling-exploration:adventure_title")
        if (imageId.includes(':')) {
            const parts = imageId.split(':');
            if (parts.length !== 2)
                return false;
            const [adventureId, localId] = parts;
            return /^[a-z0-9-]+$/.test(adventureId) && /^[a-z0-9_-]+$/.test(localId);
        }
        return /^[a-z0-9_-]+$/.test(imageId);
    }
    parseScenes() {
        const scenes = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.SCENE) {
                const scene = this.parseScene();
                if (scene) {
                    scenes.push(scene);
                }
            }
            this.advance();
        }
        return scenes;
    }
    parseScene() {
        const id = this.currentToken().value;
        this.currentSceneId = id;
        this.currentSectionIndex = 0;
        this.currentOptionIndex = 0;
        this.advance();
        let style;
        let autoEffect;
        let locationTag;
        const sections = [];
        const startIndex = this.currentTokenIndex;
        const endIndex = this.findSceneEnd(startIndex);
        // Parse scene-level fields first (Style, Auto Effects) - must be before first description/option
        let sceneLevelFieldsEnd = startIndex;
        for (let i = startIndex; i < endIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION ||
                this.tokens[i].type === Tokenizer_1.TokenType.OPTION) {
                sceneLevelFieldsEnd = i;
                break;
            }
        }
        // Find style (must be before first description/option)
        for (let i = startIndex; i < sceneLevelFieldsEnd; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.STYLE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const styleText = this.tokens[i + 1].value;
                    const parsedStyle = (0, styleValidation_1.parseStyleText)(styleText);
                    if (parsedStyle) {
                        const validation = (0, styleValidation_1.validateCustomStyle)(parsedStyle, `Scene '${id}' style`);
                        if (validation.isValid && validation.sanitizedStyle) {
                            style = validation.sanitizedStyle;
                        }
                        // Add any errors or warnings
                        this.errors.push(...(0, styleValidation_1.styleValidationToParseErrors)(validation, this.tokens[i].line));
                    }
                }
                break;
            }
        }
        // Find auto effects (must be before first description/option)
        for (let i = startIndex; i < sceneLevelFieldsEnd; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.AUTO_EFFECTS) {
                if (i + 1 < this.tokens.length) {
                    if (this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                        // New script format
                        autoEffect = this.tokens[i + 1].value;
                    }
                    else if (this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                        // Legacy format - reject
                        this.addError(this.tokens[i].line, `Auto Effects must use script format with triple single quotes. Old format is no longer supported. Use: Auto Effects: '''script'''`, 'validation');
                    }
                }
                break;
            }
        }
        // Find location tag (must be before first description/option)
        for (let i = startIndex; i < sceneLevelFieldsEnd; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.LOCATION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const tagValue = this.tokens[i + 1].value.trim();
                    locationTag = tagValue.length > 0 ? tagValue : undefined;
                }
                break;
            }
        }
        // Find variables (anywhere in the scene, not just before description)
        let variables;
        for (let i = startIndex; i < endIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.VARIABLES) {
                // Store current position and parse variables
                const savedIndex = this.currentTokenIndex;
                this.currentTokenIndex = i;
                this.advance(); // Skip the "Variables:" token
                variables = this.parseVariables('scene');
                this.currentTokenIndex = savedIndex; // Restore position
                break;
            }
        }
        // Validate scene-level field order
        this.validateSceneFieldOrder(id, startIndex, sceneLevelFieldsEnd);
        // Also check for Style/Auto Effects blocks that come after Description/Option blocks
        this.validateSceneFieldsAfterContent(id, sceneLevelFieldsEnd, endIndex);
        // Parse multiple description-option sections
        let currentIndex = sceneLevelFieldsEnd;
        this.currentSectionIndex = 0;
        while (currentIndex < endIndex) {
            const section = this.parseSection(currentIndex, endIndex);
            if (section) {
                sections.push(section);
                // Move past this section
                currentIndex = this.findSectionEnd(currentIndex, endIndex);
                this.currentSectionIndex++;
            }
            else {
                // If no section was created, we might have options without description
                // Check if there are options at this position
                if (this.tokens[currentIndex].type === Tokenizer_1.TokenType.OPTION) {
                    const optionsSection = this.parseOptionsOnlySection(currentIndex, endIndex);
                    if (optionsSection) {
                        sections.push(optionsSection);
                        currentIndex = this.findOptionsSectionEnd(currentIndex, endIndex);
                        this.currentSectionIndex++;
                    }
                    else {
                        currentIndex++;
                    }
                }
                else {
                    currentIndex++;
                }
            }
        }
        // If no sections were parsed, create a default section (for backward compatibility)
        if (sections.length === 0) {
            sections.push({
                options: []
            });
        }
        return {
            id,
            style,
            autoEffect,
            locationTag,
            sections,
            variables
        };
    }
    findSceneEnd(startIndex) {
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.SCENE) {
                return i;
            }
        }
        return this.tokens.length;
    }
    findSectionEnd(startIndex, sceneEndIndex) {
        // Find the next description or end of scene
        for (let i = startIndex + 1; i < sceneEndIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                return i;
            }
        }
        return sceneEndIndex;
    }
    findOptionsSectionEnd(startIndex, sceneEndIndex) {
        // Find the next description or end of scene
        for (let i = startIndex + 1; i < sceneEndIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                return i;
            }
        }
        return sceneEndIndex;
    }
    parseOptionsOnlySection(startIndex, sceneEndIndex) {
        const options = [];
        // Reset option index for this section
        this.currentOptionIndex = 0;
        // Find options for this section
        for (let i = startIndex; i < sceneEndIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.OPTION) {
                const option = this.parseOptionAt(i);
                if (option) {
                    options.push(option);
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                // Next section starts
                break;
            }
        }
        // Only create section if it has options
        if (options.length > 0) {
            return {
                options
            };
        }
        return null;
    }
    parseSection(startIndex, sceneEndIndex) {
        let description;
        const options = [];
        // Reset option index for this section
        this.currentOptionIndex = 0;
        // Find description for this section
        if (startIndex < sceneEndIndex && this.tokens[startIndex].type === Tokenizer_1.TokenType.DESCRIPTION) {
            if (startIndex + 1 < this.tokens.length && this.tokens[startIndex + 1].type === Tokenizer_1.TokenType.TEXT) {
                const descriptionText = this.tokens[startIndex + 1].value;
                description = RichTextParser_1.RichTextParser.parseRichTextDescription(descriptionText);
                // Extract inline options from description and assign them optionId values
                // but do NOT add them to the regular options array - they will be rendered inline
                this.extractInlineOptionsFromDescription(description, this.currentSceneId, this.currentSectionIndex);
            }
            startIndex += 2; // Move past description and text
        }
        // Find options for this section
        for (let i = startIndex; i < sceneEndIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.OPTION) {
                const option = this.parseOptionAt(i);
                if (option) {
                    options.push(option);
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                // Next section starts
                break;
            }
        }
        // Only create section if it has options or description
        if (description !== undefined || options.length > 0) {
            return {
                description,
                options
            };
        }
        return null;
    }
    parseOptionAt(index) {
        if (index + 1 >= this.tokens.length)
            return null;
        let text = '';
        let target = '';
        let condition;
        let effect;
        let waitMessage;
        let autoTransition;
        let isBackOption;
        // Parse option text as rich text
        if (this.tokens[index + 1].type === Tokenizer_1.TokenType.TEXT) {
            const optionText = this.tokens[index + 1].value;
            text = RichTextParser_1.RichTextParser.parseRichTextDescription(optionText);
        }
        else {
            // No text found - likely missing triple-quote delimiters
            this.addError(this.tokens[index].line, 'Option must use triple-quote delimiters: Option: """text"""', 'validation');
            return null;
        }
        // Validate that text is not empty
        if (typeof text === 'object' && text.elements.length === 0) {
            this.addError(this.tokens[index].line, 'Option text cannot be empty', 'validation');
            return null;
        }
        // Look for target, conditions, effects, wait message, auto-transition
        for (let i = index + 2; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.TARGET) {
                if (i + 1 < this.tokens.length) {
                    if (this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                        // Script target format: Target: '''script'''
                        // Store with delimiters for runtime detection
                        target = this.tokens[i + 1].value;
                    }
                    else if (this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                        // Static target format: Target: scene_id
                        target = this.tokens[i + 1].value;
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.CONDITIONS) {
                if (i + 1 < this.tokens.length) {
                    if (this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                        // New script format
                        condition = this.tokens[i + 1].value;
                    }
                    else if (this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                        // Legacy format - reject
                        this.addError(this.tokens[i].line, `Conditions must use script format with triple single quotes. Old format is no longer supported. Use: Conditions: '''script'''`, 'validation');
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.EFFECTS) {
                if (i + 1 < this.tokens.length) {
                    if (this.tokens[i + 1].type === Tokenizer_1.TokenType.SCRIPT_STRING) {
                        // New script format
                        effect = this.tokens[i + 1].value;
                    }
                    else if (this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                        // Legacy format - reject
                        this.addError(this.tokens[i].line, `Effects must use script format with triple single quotes. Old format is no longer supported. Use: Effects: '''script'''`, 'validation');
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.WAIT_MESSAGE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const waitMessageText = this.tokens[i + 1].value;
                    waitMessage = RichTextParser_1.RichTextParser.parseRichTextDescription(waitMessageText);
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.AUTO_TRANSITION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const autoTransitionValue = this.tokens[i + 1].value.toLowerCase();
                    if (autoTransitionValue === 'true') {
                        autoTransition = true;
                    }
                    else if (autoTransitionValue === 'false') {
                        autoTransition = false;
                    }
                    else {
                        this.addError(this.tokens[i].line, `Auto-Transition must be 'true' or 'false'. Got: '${this.tokens[i + 1].value}'`, 'validation');
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.BACK_OPTION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const backOptionValue = this.tokens[i + 1].value.toLowerCase();
                    if (backOptionValue === 'true' || backOptionValue === '1') {
                        isBackOption = true;
                    }
                    else if (backOptionValue === 'false' || backOptionValue === '0') {
                        isBackOption = false;
                    }
                    else {
                        this.addError(this.tokens[i].line, `Back Option must be 'true' or 'false'. Got: '${this.tokens[i + 1].value}'`, 'validation');
                    }
                }
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.OPTION ||
                this.tokens[i].type === Tokenizer_1.TokenType.SECTION ||
                this.tokens[i].type === Tokenizer_1.TokenType.SCENE) {
                break;
            }
        }
        if (!target) {
            // Get string representation of text for error message
            const textStr = typeof text === 'string' ? text : text.elements.map(e => {
                if (typeof e.content === 'string')
                    return e.content;
                return '';
            }).join('');
            this.addError(this.tokens[index].line, `Option '${textStr}' missing target`, 'validation');
            return null;
        }
        // Validate that auto-transition can only be set on waiting options
        // A waiting option must have both Wait Message and Conditions
        if (autoTransition === true) {
            const hasWaitMessage = !!waitMessage;
            const hasCondition = !!condition;
            if (!hasWaitMessage || !hasCondition) {
                // Get string representation of text for error message
                const textStr = typeof text === 'string' ? text : text.elements.map(e => {
                    if (typeof e.content === 'string')
                        return e.content;
                    return '';
                }).join('');
                const missingFields = [];
                if (!hasWaitMessage)
                    missingFields.push('Wait Message');
                if (!hasCondition)
                    missingFields.push('Conditions');
                this.addError(this.tokens[index].line, `Option '${textStr}' has Auto-Transition: true but is missing ${missingFields.join(' and ')}. Auto-Transition can only be used on waiting options (options with both Wait Message and Conditions).`, 'validation');
            }
        }
        const optionId = this.generateOptionId(this.currentSceneId, this.currentSectionIndex, this.currentOptionIndex);
        this.currentOptionIndex++;
        return {
            id: optionId,
            text,
            target,
            condition,
            effect,
            waitMessage,
            autoTransition,
            ...(isBackOption ? { isBackOption: true } : {})
        };
    }
    parseItems() {
        const items = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.ITEM) {
                const item = this.parseItem();
                if (item) {
                    items.push(item);
                }
            }
            this.advance();
        }
        return items;
    }
    parseItem() {
        const tag = this.currentToken().value;
        this.advance();
        let name = '';
        let description = '';
        let iconId = '';
        let maxIndex = this.currentTokenIndex;
        const startIndex = this.currentTokenIndex;
        // Find name
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.NAME) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    name = this.tokens[i + 1].value;
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find icon
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.ICON) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    iconId = this.tokens[i + 1].value;
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find description
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const descriptionText = this.tokens[i + 1].value;
                    // Parse as rich text description
                    description = RichTextParser_1.RichTextParser.parseRichTextDescription(descriptionText);
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find variables
        let variables;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.VARIABLES) {
                // Store current position and parse variables
                const savedIndex = this.currentTokenIndex;
                this.currentTokenIndex = i;
                this.advance(); // Skip the "Variables:" token
                variables = this.parseVariables('item');
                this.currentTokenIndex = savedIndex; // Restore position
                maxIndex = Math.max(maxIndex, i);
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Advance to the end of this item
        while (this.currentTokenIndex < maxIndex) {
            this.advance();
        }
        // Validate that all required fields are present
        if (!name) {
            this.addError(this.currentToken().line, `Item '${tag}' missing name`, 'validation');
            return null;
        }
        if (!iconId) {
            this.addError(this.currentToken().line, `Item '${tag}' missing icon`, 'validation');
            return null;
        }
        if (!this.isValidIconId(iconId)) {
            this.addError(this.currentToken().line, `Item '${tag}' has invalid icon ID format: '${iconId}'`, 'validation');
            return null;
        }
        if (!description || (typeof description === 'object' && description.elements.length === 0)) {
            this.addError(this.currentToken().line, `Item '${tag}' missing description`, 'validation');
            return null;
        }
        return {
            tag,
            name,
            description,
            iconId,
            variables
        };
    }
    isValidIconId(iconId) {
        if (!iconId || iconId.trim() === '') {
            return false;
        }
        // Check for namespaced format: adventure-id:icon-id
        if (iconId.includes(':')) {
            const parts = iconId.split(':');
            if (parts.length !== 2) {
                return false;
            }
            const [namespace, iconName] = parts;
            return this.isValidIconName(namespace) && this.isValidIconName(iconName);
        }
        // Simple icon ID format
        return this.isValidIconName(iconId);
    }
    isValidIconName(name) {
        if (!name || name.trim() === '') {
            return false;
        }
        // Icon names should contain only lowercase letters, numbers, underscores, and hyphens
        const validPattern = /^[a-z0-9_-]+$/;
        return validPattern.test(name);
    }
    parseLocations() {
        const locations = [];
        while (this.currentToken().type !== Tokenizer_1.TokenType.EOF &&
            this.currentToken().type !== Tokenizer_1.TokenType.SECTION) {
            if (this.currentToken().type === Tokenizer_1.TokenType.ITEM) { // Reuse ITEM token for location parsing
                const location = this.parseLocation();
                if (location) {
                    locations.push(location);
                }
            }
            this.advance();
        }
        return locations;
    }
    parseLocation() {
        const tag = this.currentToken().value;
        this.advance();
        let name = '';
        let description = '';
        let locationImageId;
        let startingInventory = [];
        let maxIndex = this.currentTokenIndex;
        const startIndex = this.currentTokenIndex;
        // Find name
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.NAME) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    name = this.tokens[i + 1].value;
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find location image
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.LOCATION_IMAGE) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    locationImageId = this.tokens[i + 1].value;
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find description
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.DESCRIPTION) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const descriptionText = this.tokens[i + 1].value;
                    // Parse as rich text description
                    description = RichTextParser_1.RichTextParser.parseRichTextDescription(descriptionText);
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find starting inventory
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.STARTING_INVENTORY) {
                if (i + 1 < this.tokens.length && this.tokens[i + 1].type === Tokenizer_1.TokenType.TEXT) {
                    const inventoryText = this.tokens[i + 1].value;
                    startingInventory = inventoryText.split(',').map(item => item.trim()).filter(item => item.length > 0);
                    maxIndex = Math.max(maxIndex, i + 1);
                }
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Find variables
        let variables;
        for (let i = startIndex; i < this.tokens.length; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.VARIABLES) {
                // Store current position and parse variables
                const savedIndex = this.currentTokenIndex;
                this.currentTokenIndex = i;
                this.advance(); // Skip the "Variables:" token
                variables = this.parseVariables('location');
                this.currentTokenIndex = savedIndex; // Restore position
                maxIndex = Math.max(maxIndex, i);
                break;
            }
            if (this.tokens[i].type === Tokenizer_1.TokenType.SECTION || this.tokens[i].type === Tokenizer_1.TokenType.ITEM) {
                break;
            }
        }
        // Advance to the end of this location
        while (this.currentTokenIndex < maxIndex) {
            this.advance();
        }
        // Validate that all required fields are present
        if (!name) {
            this.addError(this.currentToken().line, `Location '${tag}' missing name`, 'validation');
            return null;
        }
        if (!description || (typeof description === 'object' && description.elements.length === 0)) {
            this.addError(this.currentToken().line, `Location '${tag}' missing description`, 'validation');
            return null;
        }
        return {
            tag,
            name,
            description,
            locationImageId,
            startingInventory,
            variables
        };
    }
    parseStats() {
        return [];
    }
    currentToken() {
        return this.tokens[this.currentTokenIndex] || { type: Tokenizer_1.TokenType.EOF, value: '', line: 0, column: 0 };
    }
    advance() {
        if (this.currentTokenIndex < this.tokens.length) {
            this.currentTokenIndex++;
        }
    }
    addError(line, message, type) {
        this.errors.push({ line, message, type });
    }
    createEmptyAdventure() {
        return {
            title: '',
            description: RichTextParser_1.RichTextParser.parseRichTextDescription(''),
            characters: [],
            players: [],
            scenes: [],
            items: [],
            locations: [],
            stats: [],
            activityTypes: [],
            activities: [],
            entityTypes: [],
            entityDependentSceneIds: []
        };
    }
    generateOptionId(sceneId, sectionIndex, optionIndex) {
        return `${sceneId}_section${sectionIndex}_option${optionIndex}`;
    }
    generateInlineOptionId(sceneId, sectionIndex, inlineIndex) {
        return `${sceneId}_section${sectionIndex}_inline_${inlineIndex}`;
    }
    /**
     * Extract inline options from a rich text description and assign them optionId values
     * This method does NOT return the options - they are only rendered inline
     */
    extractInlineOptionsFromDescription(description, sceneId, sectionIndex) {
        let inlineIndex = 0;
        // Handle string descriptions (no inline options)
        if (typeof description === 'string') {
            return;
        }
        // Recursively search through the description elements for inline-option types
        const extractFromElements = (elements) => {
            for (const element of elements) {
                if (element.type === 'inline-option') {
                    // Generate optionId for the inline option
                    const optionId = this.generateInlineOptionId(sceneId, sectionIndex, inlineIndex);
                    // Add the optionId to the RichTextElement so the frontend can use it
                    element.optionId = optionId;
                    inlineIndex++;
                }
                else if (element.type === 'conditional') {
                    if (element.children && element.children.length > 0) {
                        extractFromElements(element.children);
                    }
                    if (element.elseChildren && element.elseChildren.length > 0) {
                        extractFromElements(element.elseChildren);
                    }
                }
                else if (element.content && Array.isArray(element.content)) {
                    // Recursively search nested content
                    extractFromElements(element.content);
                }
            }
        };
        extractFromElements(description.elements);
    }
    /**
     * Render inline option content to plain text for the option text
     */
    renderInlineOptionText(content) {
        return content.map(element => {
            if (element.type === 'text') {
                return element.content;
            }
            else if (element.type === 'bold') {
                return `**${element.content}**`;
            }
            else if (element.type === 'italic') {
                return `*${element.content}*`;
            }
            else if (element.type === 'image') {
                // For images, use a placeholder text
                return `[${element.imageType}]`;
            }
            else if (Array.isArray(element.content)) {
                return this.renderInlineOptionText(element.content);
            }
            return '';
        }).join('');
    }
    validateSceneFieldOrder(sceneId, startIndex, endIndex) {
        let styleIndex = -1;
        let autoEffectsIndex = -1;
        let locationIndex = -1;
        let descriptionIndex = -1;
        let optionIndex = -1;
        // Find the positions of each field type
        for (let i = startIndex; i < endIndex; i++) {
            switch (this.tokens[i].type) {
                case Tokenizer_1.TokenType.STYLE:
                    styleIndex = i;
                    break;
                case Tokenizer_1.TokenType.AUTO_EFFECTS:
                    autoEffectsIndex = i;
                    break;
                case Tokenizer_1.TokenType.LOCATION:
                    locationIndex = i;
                    break;
                case Tokenizer_1.TokenType.DESCRIPTION:
                    descriptionIndex = i;
                    break;
                case Tokenizer_1.TokenType.OPTION:
                    if (optionIndex === -1) {
                        optionIndex = i;
                    }
                    break;
            }
        }
        // Note: Style and Auto Effects can be in any order relative to each other,
        // as long as they both come before the first Description block
        // Check if Style comes after Description or Options (incorrect order)
        if (styleIndex !== -1) {
            if (descriptionIndex !== -1 && styleIndex > descriptionIndex) {
                this.addError(this.tokens[styleIndex].line, `Scene '${sceneId}': Style block must come before Description blocks. Style found at line ${this.tokens[styleIndex].line} but Description found at line ${this.tokens[descriptionIndex].line}. Correct order: Style and Auto Effects (any order) → Description → Options.`, 'validation');
            }
            if (optionIndex !== -1 && styleIndex > optionIndex) {
                this.addError(this.tokens[styleIndex].line, `Scene '${sceneId}': Style block must come before Option blocks. Style found at line ${this.tokens[styleIndex].line} but Option found at line ${this.tokens[optionIndex].line}. Correct order: Style and Auto Effects (any order) → Description → Options.`, 'validation');
            }
        }
        // Check if Auto Effects comes after Description or Options (incorrect order)
        if (autoEffectsIndex !== -1) {
            if (descriptionIndex !== -1 && autoEffectsIndex > descriptionIndex) {
                this.addError(this.tokens[autoEffectsIndex].line, `Scene '${sceneId}': Auto Effects block must come before Description blocks. Auto Effects found at line ${this.tokens[autoEffectsIndex].line} but Description found at line ${this.tokens[descriptionIndex].line}. Correct order: Style and Auto Effects (any order) → Description → Options.`, 'validation');
            }
            if (optionIndex !== -1 && autoEffectsIndex > optionIndex) {
                this.addError(this.tokens[autoEffectsIndex].line, `Scene '${sceneId}': Auto Effects block must come before Option blocks. Auto Effects found at line ${this.tokens[autoEffectsIndex].line} but Option found at line ${this.tokens[optionIndex].line}. Correct order: Style and Auto Effects (any order) → Description → Options.`, 'validation');
            }
        }
        // Check if Location comes after Description or Options (incorrect order)
        if (locationIndex !== -1) {
            if (descriptionIndex !== -1 && locationIndex > descriptionIndex) {
                this.addError(this.tokens[locationIndex].line, `Scene '${sceneId}': Location block must come before Description blocks. Location found at line ${this.tokens[locationIndex].line} but Description found at line ${this.tokens[descriptionIndex].line}. Correct order: Style, Auto Effects, Location (any order) → Description → Options.`, 'validation');
            }
            if (optionIndex !== -1 && locationIndex > optionIndex) {
                this.addError(this.tokens[locationIndex].line, `Scene '${sceneId}': Location block must come before Option blocks. Location found at line ${this.tokens[locationIndex].line} but Option found at line ${this.tokens[optionIndex].line}. Correct order: Style, Auto Effects, Location (any order) → Description → Options.`, 'validation');
            }
        }
    }
    validateSceneFieldsAfterContent(sceneId, startIndex, endIndex) {
        // Look for Style, Auto Effects, and Location blocks that come after Description/Option blocks
        for (let i = startIndex; i < endIndex; i++) {
            if (this.tokens[i].type === Tokenizer_1.TokenType.STYLE) {
                this.addError(this.tokens[i].line, `Scene '${sceneId}': Style block found after Description or Option blocks at line ${this.tokens[i].line}. Style blocks must come before the first Description block.`, 'validation');
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.AUTO_EFFECTS) {
                this.addError(this.tokens[i].line, `Scene '${sceneId}': Auto Effects block found after Description or Option blocks at line ${this.tokens[i].line}. Auto Effects blocks must come before the first Description block.`, 'validation');
            }
            else if (this.tokens[i].type === Tokenizer_1.TokenType.LOCATION) {
                this.addError(this.tokens[i].line, `Scene '${sceneId}': Location block found after Description or Option blocks at line ${this.tokens[i].line}. Location blocks must come before the first Description block.`, 'validation');
            }
        }
    }
    /**
     * Validate inline options in adventure descriptions
     */
    validateInlineOptions(adventure) {
        const sceneIds = new Set(adventure.scenes.map(scene => scene.id));
        // Validate inline options in adventure description
        this.validateInlineOptionsInDescription(adventure.description, 'Adventure description', sceneIds);
        // Validate inline options in character descriptions
        for (const character of adventure.characters) {
            this.validateInlineOptionsInDescription(character.description, `Character '${character.name}' description`, sceneIds);
        }
        // Validate inline options in scene descriptions
        for (const scene of adventure.scenes) {
            for (let sectionIndex = 0; sectionIndex < scene.sections.length; sectionIndex++) {
                const section = scene.sections[sectionIndex];
                if (section.description) {
                    this.validateInlineOptionsInDescription(section.description, `Scene '${scene.id}' section ${sectionIndex + 1} description`, sceneIds);
                }
            }
        }
        // Validate inline options in item descriptions
        for (const item of adventure.items || []) {
            this.validateInlineOptionsInDescription(item.description, `Item '${item.tag}' description`, sceneIds);
        }
    }
    /**
     * Validate inline option targets against the current scene list. Call this after required-scenes
     * injection so that targets like session_info (injected from required_scenes) are present.
     * Returns a list of parse errors for any inline option that targets a non-existent scene.
     */
    static validateInlineOptionTargets(adventure) {
        const errors = [];
        const sceneIds = new Set((adventure.scenes || []).map((s) => s.id));
        const checkElement = (element, context) => {
            if (element.type === 'inline-option') {
                if (element.target && !sceneIds.has(element.target)) {
                    const isScriptExpression = element.target.includes("'''") || element.target.includes('+') || element.target.includes('getVariable') || element.target.includes('getAdventureInvite');
                    const isSpecialTarget = element.target === 'FINISH' || element.target === 'LEAVE' || element.target === 'MAIN_MENU' || element.target === 'QUIT_ADVENTURE' || element.target === 'LOGGED_IN' || element.target === 'RETURN_TO_ADVENTURE' || element.target === 'DESTROY_SESSION' || element.target.startsWith('START_ADVENTURE:') || element.target.startsWith('START_OR_RESUME_ADVENTURE:') || element.target.startsWith('JOIN_ADVENTURE:') || (isScriptExpression && (element.target.includes('START_ADVENTURE') || element.target.includes('START_OR_RESUME_ADVENTURE') || element.target.includes('JOIN_ADVENTURE') || element.target.includes('RETURN_TO_ADVENTURE') || element.target.includes('DESTROY_SESSION')));
                    if (!isSpecialTarget && !isScriptExpression && !element.target.startsWith('${')) {
                        errors.push({ line: 0, message: `${context}: Inline option targets non-existent scene '${element.target}'`, type: 'validation' });
                    }
                }
            }
            else if (element.type === 'conditional') {
                if (element.children) {
                    for (const child of element.children) {
                        checkElement(child, context);
                    }
                }
                if (element.elseChildren) {
                    for (const child of element.elseChildren) {
                        checkElement(child, context);
                    }
                }
                return;
            }
            if (Array.isArray(element.content)) {
                for (const nested of element.content) {
                    if (nested && typeof nested === 'object' && 'type' in nested) {
                        checkElement(nested, context);
                    }
                }
            }
        };
        const checkDescription = (description, context) => {
            if (typeof description === 'string')
                return;
            for (const element of description.elements) {
                checkElement(element, context);
            }
        };
        checkDescription(adventure.description, 'Adventure description');
        for (const character of adventure.characters || []) {
            checkDescription(character.description, `Character '${character.name}' description`);
        }
        for (const scene of adventure.scenes || []) {
            for (let i = 0; i < scene.sections.length; i++) {
                const section = scene.sections[i];
                if (section.description) {
                    checkDescription(section.description, `Scene '${scene.id}' section ${i + 1} description`);
                }
            }
        }
        for (const item of adventure.items || []) {
            checkDescription(item.description, `Item '${item.tag}' description`);
        }
        return errors;
    }
    /**
     * Validate inline options in a specific description
     */
    validateInlineOptionsInDescription(description, context, sceneIds) {
        if (typeof description === 'string') {
            return; // Plain text descriptions don't have inline options
        }
        for (const element of description.elements) {
            this.validateInlineOptionsInDescriptionElement(element, context, sceneIds);
        }
    }
    validateInlineOptionsInDescriptionElement(element, context, sceneIds) {
        if (element.type === 'inline-option') {
            if (element.target && !sceneIds.has(element.target)) {
                const isScriptExpression = element.target.includes("'''") || element.target.includes('+') || element.target.includes('getVariable') || element.target.includes('getAdventureInvite');
                const isSpecialTarget = element.target === 'FINISH' ||
                    element.target === 'LEAVE' ||
                    element.target === 'MAIN_MENU' ||
                    element.target === 'QUIT_ADVENTURE' ||
                    element.target === 'LOGGED_IN' ||
                    element.target === 'RETURN_TO_ADVENTURE' ||
                    element.target === 'DESTROY_SESSION' ||
                    element.target.startsWith('START_ADVENTURE:') ||
                    element.target.startsWith('START_OR_RESUME_ADVENTURE:') ||
                    element.target.startsWith('JOIN_ADVENTURE:') ||
                    (isScriptExpression && (element.target.includes('START_ADVENTURE') || element.target.includes('START_OR_RESUME_ADVENTURE') || element.target.includes('JOIN_ADVENTURE') || element.target.includes('RETURN_TO_ADVENTURE') || element.target.includes('DESTROY_SESSION')));
                if (!isSpecialTarget && !isScriptExpression && !element.target.startsWith('${')) {
                    this.addError(0, `${context}: Inline option targets non-existent scene '${element.target}'`, 'validation');
                }
            }
        }
        else if (element.type === 'conditional') {
            if (element.children) {
                for (const child of element.children) {
                    this.validateInlineOptionsInDescriptionElement(child, context, sceneIds);
                }
            }
            if (element.elseChildren) {
                for (const child of element.elseChildren) {
                    this.validateInlineOptionsInDescriptionElement(child, context, sceneIds);
                }
            }
            return;
        }
        if (Array.isArray(element.content)) {
            for (const nested of element.content) {
                if (nested && typeof nested === 'object' && 'type' in nested) {
                    this.validateInlineOptionsInDescriptionElement(nested, context, sceneIds);
                }
            }
        }
    }
    /**
     * Validate adventure tags in adventure descriptions
     */
    validateAdventureTags(adventure) {
        // Validate adventure tags in adventure description
        this.validateAdventureTagsInDescription(adventure.description, 'Adventure description');
        // Validate adventure tags in character descriptions
        for (const character of adventure.characters) {
            this.validateAdventureTagsInDescription(character.description, `Character '${character.name}' description`);
        }
        // Validate adventure tags in scene descriptions
        for (const scene of adventure.scenes) {
            for (let sectionIndex = 0; sectionIndex < scene.sections.length; sectionIndex++) {
                const section = scene.sections[sectionIndex];
                if (section.description) {
                    this.validateAdventureTagsInDescription(section.description, `Scene '${scene.id}' section ${sectionIndex + 1} description`);
                }
            }
        }
        // Validate adventure tags in item descriptions
        for (const item of adventure.items || []) {
            this.validateAdventureTagsInDescription(item.description, `Item '${item.tag}' description`);
        }
        // Validate adventure tags in location descriptions
        for (const location of adventure.locations || []) {
            this.validateAdventureTagsInDescription(location.description, `Location '${location.tag}' description`);
        }
    }
    /**
     * Validate adventure tags in a specific description
     */
    validateAdventureTagsInDescription(description, context) {
        if (typeof description === 'string') {
            return; // Plain text descriptions don't have adventure tags
        }
        const validateElement = (element) => {
            if (element.type === 'adventure') {
                if (element.adventureId) {
                    // Validate adventure ID format
                    if (!this.isValidAdventureId(element.adventureId)) {
                        this.addError(0, `${context}: Invalid adventure ID format in <adventure:${element.adventureId}>. Adventure IDs must be lowercase alphanumeric with hyphens (e.g., 'dragon-slayer')`, 'warning');
                    }
                    // Note: We can't validate existence here since AdventureParser doesn't have access to AdventureManager
                    // Existence validation would happen at runtime or in a separate validation pass
                }
            }
            else if (Array.isArray(element.content)) {
                // Recursively check nested elements
                for (const nestedElement of element.content) {
                    validateElement(nestedElement);
                }
            }
        };
        for (const element of description.elements) {
            validateElement(element);
        }
    }
    /**
     * Validate adventure ID format
     */
    isValidAdventureId(adventureId) {
        // Adventure IDs must be lowercase alphanumeric with hyphens only
        return /^[a-z0-9-]+$/.test(adventureId) && adventureId.length >= 3;
    }
    /**
     * Validate player definitions and open character / open group rules.
     */
    validatePlayerDefinitions(adventure) {
        if (!adventure.players || adventure.players.length === 0) {
            this.addError(0, 'Adventure must define at least one player in the ## Players section', 'validation');
            return;
        }
        const charactersByName = new Map(adventure.characters.map(c => [c.name, c]));
        const characterNames = new Set(charactersByName.keys());
        for (const character of adventure.characters) {
            if ((0, characterInstanceNames_1.authoredCharacterNameContainsForbiddenHash)(character.name)) {
                this.addError(0, `Character name '${character.name}' must not contain '#'. The '#' character is reserved for runtime open-character instances (e.g. Musician#1).`, 'validation');
            }
            if (character.kind === 'open' && character.isNpc) {
                this.addError(0, `Character '${character.name}' cannot be both Kind: open and IsNpc: true. Open templates are for human seats only.`, 'validation');
            }
        }
        const openGroups = adventure.players.filter(p => p.kind === 'open');
        if (openGroups.length > 1) {
            this.addError(0, `Adventure may define at most one open player group (Kind: open). Found: ${openGroups.map(g => g.playerName).join(', ')}.`, 'validation');
        }
        const fixedStartingCharacters = new Set();
        for (const player of adventure.players) {
            const isOpen = player.kind === 'open';
            const starting = charactersByName.get(player.startingCharacter);
            if (!characterNames.has(player.startingCharacter)) {
                this.addError(0, `Player '${player.playerName}' references non-existent character '${player.startingCharacter}'`, 'validation');
            }
            if (isOpen) {
                if (player.min === undefined) {
                    this.addError(0, `Open player group '${player.playerName}' must declare Min: (non-negative integer).`, 'validation');
                }
                else if (player.min < 0) {
                    this.addError(0, `Open player group '${player.playerName}' has invalid Min: ${player.min}. Min must be ≥ 0.`, 'validation');
                }
                if (player.max === undefined) {
                    this.addError(0, `Open player group '${player.playerName}' must declare Max: (integer ≥ 1).`, 'validation');
                }
                else if (player.max < 1) {
                    this.addError(0, `Open player group '${player.playerName}' has invalid Max: ${player.max}. Max must be ≥ 1.`, 'validation');
                }
                if (player.min !== undefined &&
                    player.max !== undefined &&
                    player.max < player.min) {
                    this.addError(0, `Open player group '${player.playerName}' has Max (${player.max}) less than Min (${player.min}).`, 'validation');
                }
                if (starting && starting.kind !== 'open') {
                    this.addError(0, `Open player group '${player.playerName}' Starting Character '${player.startingCharacter}' must be Kind: open.`, 'validation');
                }
            }
            else {
                // Fixed slot: unique starting characters among fixed slots only
                if (fixedStartingCharacters.has(player.startingCharacter)) {
                    this.addError(0, `Multiple fixed players assigned to character '${player.startingCharacter}'. Each fixed player must have a unique starting character.`, 'validation');
                }
                else {
                    fixedStartingCharacters.add(player.startingCharacter);
                }
                if (starting?.kind === 'open') {
                    this.addError(0, `Fixed player '${player.playerName}' cannot use open character template '${player.startingCharacter}' as Starting Character. Open templates are only for Kind: open player groups.`, 'validation');
                }
            }
        }
    }
    /**
     * Parse Variables block
     */
    parseVariables(ownerScope) {
        const variables = [];
        // Skip newlines
        while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
            this.advance();
        }
        // Parse variable declarations
        while (this.currentToken().type === Tokenizer_1.TokenType.VARIABLE_DECLARATION) {
            const variable = this.parseVariableDeclaration(this.currentToken().value, ownerScope);
            if (variable) {
                variables.push(variable);
            }
            this.advance();
            // Skip newlines
            while (this.currentToken().type === Tokenizer_1.TokenType.NEWLINE) {
                this.advance();
            }
        }
        return variables;
    }
    /**
     * Parse a single variable declaration
     */
    parseVariableDeclaration(line, ownerScope) {
        try {
            // Pattern: type name: "description" = value
            const match = line.match(/^\s*(\w+)\s+(\w+):\s*"([^"]*)"\s*=\s*(.+)$/);
            if (!match) {
                this.addError(this.currentToken().line, `Invalid variable declaration format: ${line}`, 'syntax');
                return null;
            }
            const [, type, name, description, defaultValueStr] = match;
            // Validate type
            if (!['string', 'number', 'boolean', 'stack', 'character_map'].includes(type)) {
                this.addError(this.currentToken().line, `Invalid variable type: ${type}. Must be string, number, boolean, stack, or character_map`, 'validation');
                return null;
            }
            // Validate name format
            if (!/^[a-z][a-z0-9_]*$/.test(name)) {
                this.addError(this.currentToken().line, `Invalid variable name format: ${name}. Must start with lowercase letter and contain only lowercase letters, numbers, and underscores`, 'validation');
                return null;
            }
            // Validate user variable naming convention
            if (ownerScope === 'user' && !name.startsWith('user_')) {
                this.addError(this.currentToken().line, `User variables must start with 'user_' prefix: ${name}`, 'validation');
                return null;
            }
            if (ownerScope === 'shared' && type === 'stack') {
                this.addError(this.currentToken().line, `Shared variables do not support stack type: ${name}`, 'validation');
                return null;
            }
            if (type === 'character_map' && !['adventure', 'scene', 'entity'].includes(ownerScope)) {
                this.addError(this.currentToken().line, `character_map is not allowed in ${ownerScope} scope: ${name}. Allowed scopes: adventure, scene, entity`, 'validation');
                return null;
            }
            // Parse default value based on type
            let defaultValue;
            switch (type) {
                case 'string':
                    // Remove surrounding quotes if present
                    let stringValue = defaultValueStr.trim();
                    if (stringValue.startsWith('"') && stringValue.endsWith('"')) {
                        stringValue = stringValue.slice(1, -1);
                    }
                    defaultValue = stringValue;
                    break;
                case 'number':
                    const numValue = parseFloat(defaultValueStr.trim());
                    if (isNaN(numValue)) {
                        this.addError(this.currentToken().line, `Invalid number value: ${defaultValueStr}`, 'validation');
                        return null;
                    }
                    defaultValue = numValue;
                    break;
                case 'boolean':
                    const boolValue = defaultValueStr.trim().toLowerCase();
                    if (boolValue === 'true') {
                        defaultValue = true;
                    }
                    else if (boolValue === 'false') {
                        defaultValue = false;
                    }
                    else {
                        this.addError(this.currentToken().line, `Invalid boolean value: ${defaultValueStr}. Must be true or false`, 'validation');
                        return null;
                    }
                    break;
                case 'stack':
                    // Stack type: default value should be JSON array string or empty array
                    let stackValue = defaultValueStr.trim();
                    if (stackValue === '' || stackValue === '[]') {
                        defaultValue = '[]';
                    }
                    else if (stackValue.startsWith('[') && stackValue.endsWith(']')) {
                        // Already a JSON array string
                        defaultValue = stackValue;
                    }
                    else {
                        // If it's quoted, remove quotes
                        if (stackValue.startsWith('"') && stackValue.endsWith('"')) {
                            stackValue = stackValue.slice(1, -1);
                        }
                        // Validate it's a JSON array
                        try {
                            const parsed = JSON.parse(stackValue);
                            if (!Array.isArray(parsed)) {
                                this.addError(this.currentToken().line, `Stack default value must be a JSON array: ${stackValue}`, 'validation');
                                return null;
                            }
                            defaultValue = stackValue;
                        }
                        catch (e) {
                            // If not valid JSON, default to empty array
                            defaultValue = '[]';
                        }
                    }
                    break;
                case 'character_map': {
                    const parsedMap = this.parseCharacterMapDefault(defaultValueStr.trim());
                    if (!parsedMap.ok) {
                        this.addError(this.currentToken().line, parsedMap.error, 'validation');
                        return null;
                    }
                    defaultValue = parsedMap.value;
                    break;
                }
                default:
                    this.addError(this.currentToken().line, `Unsupported type: ${type}`, 'validation');
                    return null;
            }
            return {
                name,
                type: type,
                description,
                defaultValue,
                ownerScope: ownerScope
            };
        }
        catch (error) {
            this.addError(this.currentToken().line, `Error parsing variable declaration: ${line}`, 'syntax');
            return null;
        }
    }
    /**
     * Parse a character_map default: `{}` or a JSON object with string values.
     */
    parseCharacterMapDefault(raw) {
        let source = raw.trim();
        if (source === '') {
            return { ok: true, value: '{}' };
        }
        if (source.startsWith('"') && source.endsWith('"') && source.length >= 2) {
            source = source.slice(1, -1);
        }
        try {
            const parsed = JSON.parse(source);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return { ok: false, error: `character_map default must be a JSON object: ${raw}` };
            }
            for (const [key, value] of Object.entries(parsed)) {
                if (typeof key !== 'string' || key.length === 0 || typeof value !== 'string') {
                    return { ok: false, error: `character_map default must have string keys and string values: ${raw}` };
                }
            }
            return { ok: true, value: JSON.stringify(parsed) };
        }
        catch {
            return { ok: false, error: `character_map default must be valid JSON object: ${raw}` };
        }
    }
    /**
     * Extract variable references from text using ${variable_name} syntax
     */
    extractVariableReferences(text) {
        const variablePattern = /\$\{([^}]+)\}/g;
        const references = [];
        let match;
        while ((match = variablePattern.exec(text)) !== null) {
            const content = match[1].trim();
            // Skip function calls (expressions containing parentheses)
            // These are script function calls, not variable references
            if (content.includes('(') && content.includes(')')) {
                continue;
            }
            // Only add simple variable references (alphanumeric with underscores)
            // This matches variable name patterns like user_player_name, selected_item, etc.
            if (/^[a-z][a-z0-9_]*$/i.test(content)) {
                references.push(content);
            }
        }
        return references;
    }
    /**
     * Validate that all variable references are declared
     */
    validateVariableReferences(adventure) {
        // Load VariablesConfig to get mandatory variables
        const variablesConfig = this.loadVariablesConfig();
        // Collect all declared variables by scope
        const declaredVariables = this.collectDeclaredVariables(adventure, variablesConfig);
        // Validate variable references in descriptions
        this.validateVariableReferencesInDescriptions(adventure, declaredVariables);
        // Validate variable references in scripts
        this.validateVariableReferencesInScripts(adventure, declaredVariables);
    }
    /**
     * Load VariablesConfig from VariablesConfig.md file
     */
    loadVariablesConfig() {
        try {
            // Load using PathConfig
            const configPath = pathConfig_1.PathConfig.getVariablesConfigPath();
            const content = fs.readFileSync(configPath, 'utf-8');
            return VariablesConfigParser_1.VariablesConfigParser.parseVariablesConfig(content);
        }
        catch (error) {
            // If file not found or error reading, log warning and return empty config
            console.warn('Could not load VariablesConfig.md:', error);
            return {
                userVariables: [],
                mandatoryVariables: {
                    player: [],
                    character: []
                }
            };
        }
    }
    /**
     * Collect all declared variables by scope
     */
    collectDeclaredVariables(adventure, variablesConfig) {
        const declaredVariables = new Map();
        // Initialize scope sets
        declaredVariables.set('user', new Set());
        declaredVariables.set('adventure', new Set());
        declaredVariables.set('player', new Set());
        declaredVariables.set('character', new Set());
        declaredVariables.set('scene', new Set());
        declaredVariables.set('item', new Set());
        declaredVariables.set('location', new Set());
        declaredVariables.set('shared', new Set());
        declaredVariables.set('entity', new Set());
        // Add user variables
        variablesConfig.userVariables.forEach(variable => {
            declaredVariables.get('user').add(variable.name);
        });
        // Add mandatory player variables
        variablesConfig.mandatoryVariables.player.forEach(variable => {
            declaredVariables.get('player').add(variable.name);
        });
        // Add mandatory character variables
        variablesConfig.mandatoryVariables.character.forEach(variable => {
            declaredVariables.get('character').add(variable.name);
        });
        // Add adventure variables
        if (adventure.variables) {
            adventure.variables.forEach(variable => {
                declaredVariables.get('adventure').add(variable.name);
            });
        }
        // Add adventure-wide character template variables (## Character Variables)
        if (adventure.characterVariables) {
            adventure.characterVariables.forEach(variable => {
                declaredVariables.get('character').add(variable.name);
            });
        }
        // Add shared variables (## Shared Variables)
        if (adventure.sharedVariables) {
            adventure.sharedVariables.forEach(variable => {
                declaredVariables.get('shared').add(variable.name);
            });
        }
        // Add entity-type variables (## Entity Types) — valid for ${name} when currentEntity is set
        if (adventure.entityTypes) {
            adventure.entityTypes.forEach(type => {
                type.variables.forEach(variable => {
                    declaredVariables.get('entity').add(variable.name);
                });
            });
        }
        // Add character variables
        adventure.characters.forEach(character => {
            if (character.variables) {
                character.variables.forEach(variable => {
                    declaredVariables.get('character').add(variable.name);
                });
            }
        });
        // Add scene variables
        adventure.scenes.forEach(scene => {
            if (scene.variables) {
                scene.variables.forEach(variable => {
                    declaredVariables.get('scene').add(variable.name);
                });
            }
        });
        // Add item variables
        if (adventure.items) {
            adventure.items.forEach(item => {
                if (item.variables) {
                    item.variables.forEach(variable => {
                        declaredVariables.get('item').add(variable.name);
                    });
                }
            });
        }
        // Add location variables
        if (adventure.locations) {
            adventure.locations.forEach(location => {
                if (location.variables) {
                    location.variables.forEach(variable => {
                        declaredVariables.get('location').add(variable.name);
                    });
                }
            });
        }
        return declaredVariables;
    }
    /**
     * Validate variable references in descriptions
     */
    validateVariableReferencesInDescriptions(adventure, declaredVariables) {
        // Validate adventure description
        this.validateVariableReferencesInText(adventure.description, 'Adventure description', declaredVariables);
        // Validate character descriptions
        adventure.characters.forEach(character => {
            this.validateVariableReferencesInText(character.description, `Character '${character.name}' description`, declaredVariables);
        });
        // Validate scene descriptions
        adventure.scenes.forEach(scene => {
            scene.sections.forEach((section, sectionIndex) => {
                if (section.description) {
                    this.validateVariableReferencesInText(section.description, `Scene '${scene.id}' section ${sectionIndex} description`, declaredVariables);
                }
                section.options.forEach(option => {
                    this.validateVariableReferencesInText(option.text, `Scene '${scene.id}' option '${option.id}' text`, declaredVariables);
                    if (option.waitMessage) {
                        this.validateVariableReferencesInText(option.waitMessage, `Scene '${scene.id}' option '${option.id}' wait message`, declaredVariables);
                    }
                });
            });
        });
        // Validate item descriptions
        if (adventure.items) {
            adventure.items.forEach(item => {
                this.validateVariableReferencesInText(item.description, `Item '${item.tag}' description`, declaredVariables);
            });
        }
        // Validate location descriptions
        if (adventure.locations) {
            adventure.locations.forEach(location => {
                this.validateVariableReferencesInText(location.description, `Location '${location.tag}' description`, declaredVariables);
            });
        }
    }
    /**
     * Validate variable references in scripts
     */
    validateVariableReferencesInScripts(adventure, declaredVariables) {
        // Validate scene auto effects
        adventure.scenes.forEach(scene => {
            if (scene.autoEffect) {
                this.validateVariableReferencesInText(scene.autoEffect, `Scene '${scene.id}' auto effect`, declaredVariables);
            }
            scene.sections.forEach(section => {
                section.options.forEach(option => {
                    if (option.condition) {
                        this.validateVariableReferencesInText(option.condition, `Scene '${scene.id}' option '${option.id}' condition`, declaredVariables);
                    }
                    if (option.effect) {
                        this.validateVariableReferencesInText(option.effect, `Scene '${scene.id}' option '${option.id}' effect`, declaredVariables);
                    }
                });
            });
        });
    }
    /**
     * Validate variable references in a single text/description
     */
    validateVariableReferencesInText(text, context, declaredVariables) {
        let textContent = '';
        if (typeof text === 'string') {
            textContent = text;
        }
        else {
            // Extract text content from rich text description
            textContent = this.extractTextFromRichTextDescription(text);
        }
        const variableReferences = this.extractVariableReferences(textContent);
        variableReferences.forEach(variableName => {
            if (!this.isVariableDeclared(variableName, declaredVariables)) {
                this.addError(0, `${context}: Variable '${variableName}' is referenced but not declared`, 'validation');
            }
        });
    }
    /**
     * Extract text content from rich text description
     */
    extractTextFromRichTextDescription(description) {
        let textContent = '';
        const extractFromElement = (element) => {
            if (element.type === 'shared-data') {
                return;
            }
            if (element.type === 'text' && typeof element.content === 'string') {
                textContent += element.content;
            }
            else if (Array.isArray(element.content)) {
                element.content.forEach(extractFromElement);
            }
        };
        description.elements.forEach(extractFromElement);
        return textContent;
    }
    /**
     * Check if a variable is declared in any scope
     */
    isVariableDeclared(variableName, declaredVariables) {
        for (const scopeVariables of declaredVariables.values()) {
            if (scopeVariables.has(variableName)) {
                return true;
            }
        }
        return false;
    }
}
exports.AdventureParser = AdventureParser;
//# sourceMappingURL=AdventureParser.js.map